import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import {
	UpgradeObjectiveAction,
	UpgradeObjectiveType,
	type ProductionValue,
	type TileEntityOnClickMethodReturnType,
	type UpgradeParams,
} from "@/types/tile";
import { ceilNumber } from "@/utils/number";
import {
	FrameName,
	tilerepoCobbledRoadsFrameNames,
	tilerepoGravelRoadsFrameNames,
	tilerepoPOIs1FrameNames,
	tilerepoSettlementBuildingsFrameNames,
	tilerepoStoneBridgeRoadsFrameNames,
	tilerepoTunnelRoadsFrameNames,
	tilerepoWaterFrameNames,
	tilerepoWoodenBridgeRoadsFrameNames,
} from ".";
import type TileEntity from "../TileEntity";
import { getProductionResourceMultiplier } from "./buildings";
import { ResourceType } from "./resources";
import { UpgradeIds } from "./upgrades";

export { settlementNames } from "./settlementNames";

// const settlementNamesByCoordinates = new Map<
// 	{ x: number; y: number },
// 	SettlementItem["name"]
// >();
// for (const [, { name, x, y }] of settlementNames)
// 	settlementNamesByCoordinates.set({ x, y }, name);

// export const getSettltmentNameByCoordinates = (
// 	coordinates: TileCoordinates,
// ): SettlementItem["name"] => {
// 	const name = settlementNamesByCoordinates.get(coordinates);
// 	if (!name) throw new Error(`Settlement at ${coordinates} not found`);
// 	return name;
// };

/** CONFIG **/

const getPopulationProduction = (base: number, entity: TileEntity): number => {
	const neighbours = entity.getOwnNeighbourTileEntities();
	const multiplier =
		1 +
		getProductionResourceMultiplier(neighbours) +
		getUpgradeCurrentReplaceValueById(UpgradeIds.town_privileges) +
		getUpgradeCurrentReplaceValueById(UpgradeIds.town_privileges2) +
		getUpgradeCurrentReplaceValueById(UpgradeIds.town_privileges3);
	return ceilNumber(base * multiplier);
};

const getCoinsProduction = (base: number): number => {
	const multiplier =
		1 + getUpgradeCurrentReplaceValueById(UpgradeIds.tax_season);
	return ceilNumber(base * multiplier);
};

type SettlementConfigItem = {
	production: Map<ResourceType, ProductionValue>;
	consumption: Map<ResourceType, number>;
	storage: Map<ResourceType, number>;
	upgrades: Map<FrameName, UpgradeParams>;
	roadCaps: {
		[FrameName.GRAVEL_ROAD]: number;
		[FrameName.COBBLED_ROAD]: number;
	};
	onClick: () => TileEntityOnClickMethodReturnType;
};

type SettlementConfig = Record<number, SettlementConfigItem>;

const config: SettlementConfig = {
	1: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(25)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(50, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 20],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.click_this_town",
							action: UpgradeObjectiveAction.SETTLEMENT_CLICK_COUNT,
							data: {
								"{number}": 20,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 500,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 100,
			},
		}),
	},
	2: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(30)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(60, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 22],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 30,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 1_000,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 120,
			},
		}),
	},
	3: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(35)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(70, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 35],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 24],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_coin_income",
							hint: "settlement.hint_3",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 200,
								"{resource}":
									ResourceType.COINS_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 2_000,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 140,
			},
		}),
	},
	4: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(40)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(80, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 40],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 26],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_2to4_towns",
							action: UpgradeObjectiveAction.SETTLEMENT_COUNT,
							data: {
								"{2to4}": 3,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.research_upgrades",
							hint: "settlement.hint_8",
							action: UpgradeObjectiveAction.UNLOCKED_UPGRADE_COUNT,
							data: {
								"{number}": 5,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 3_000,
						[ResourceType.LUMBER]: 200,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 160,
			},
		}),
	},
	5: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(45)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(100, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 28],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_2to4_houses_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_BUILDING_COUNT,
							data: {
								"{2to4}": 2,
								"{buildings}": new Set<FrameName>([
									// Tu chyba country house navyse oproti uz existujucemu configu.
									FrameName.SMALL_HOUSES,
									FrameName.MEDIUM_HOUSES,
									FrameName.LARGE_HOUSES,
									FrameName.CARPENTRY_DISTRICT,
									FrameName.STONEMASONRY_DISTRICT,
									FrameName.ENKINDLED_HOUSEHOLD,
								]),
							},
						},
					},
					price: {
						[ResourceType.COINS]: 5_000,
						[ResourceType.LUMBER]: 400,
					},
					skipPrice: {
						[ResourceType.GEMS]: 10,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 180,
			},
		}),
	},
	6: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(50)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(120, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 60],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 30],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.explore_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 250,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 8_000,
						[ResourceType.LUMBER]: 700,
					},
					skipPrice: {
						[ResourceType.GEMS]: 15,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 200,
			},
		}),
	},
	7: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(75)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(140, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 70],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 32],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_lumber_income",
							hint: "quest.hint_1005",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 100,
								"{resource}":
									ResourceType.LUMBER_UNCAPPED_INCOME,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.collect_coins_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_ONCLICK_COINS_ACCUMULATED,
							data: {
								"{number}": 25_000,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 15_000,
						[ResourceType.LUMBER]: 1_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 20,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 210,
			},
		}),
	},
	8: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(100)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(160, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 80],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 34],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.research_upgrades",
							hint: "settlement.hint_8",
							action: UpgradeObjectiveAction.UNLOCKED_UPGRADE_COUNT,
							data: {
								"{number}": 7,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 30_000,
						[ResourceType.LUMBER]: 1_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 25,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 220,
			},
		}),
	},
	9: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(125)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(180, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 90],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 36],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.explore_area_around_town",
							action: UpgradeObjectiveAction.SETTLEMENT_EXPLORED_RANGE,
							data: {
								"{number}": 4,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 50_000,
						[ResourceType.LUMBER]: 2_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 30,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 230,
			},
		}),
	},
	10: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(150)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(200, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 100],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 38],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_towns",
							action: UpgradeObjectiveAction.SETTLEMENT_COUNT,
							data: {
								"{number}": 7,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 100_000,
						[ResourceType.LUMBER]: 3_000,
						[ResourceType.STONE]: 300,
					},
					skipPrice: {
						[ResourceType.GEMS]: 35,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 10,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 240,
			},
		}),
	},
	11: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(175)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(220, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 110],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 40],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_total_workforce",
							hint: "settlement.hint_11",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 1_750,
								"{resource}": ResourceType.POPULATION_INCOME,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.have_2to4_buildings_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_BUILDING_COUNT,
							data: {
								"{2to4}": 3,
								"{buildings}":
									tilerepoSettlementBuildingsFrameNames,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 200_000,
						[ResourceType.LUMBER]: 5_000,
						[ResourceType.STONE]: 1_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 40,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 11,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 250,
			},
		}),
	},
	12: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(200)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(240, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 120],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 42],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_stone_income",
							hint: "quest.hint_1019",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 150,
								"{resource}":
									ResourceType.STONE_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 300_000,
						[ResourceType.LUMBER]: 10_000,
						[ResourceType.STONE]: 1_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 45,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 12,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 260,
			},
		}),
	},
	13: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(225)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(260, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 130],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 44],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_gravel_roads",
							hint: "settlement.hint_13",
							action: UpgradeObjectiveAction.BUILDING_COUNT,
							data: {
								"{number}": 100,
								"{buildings}": new Set<FrameName>([
									...tilerepoGravelRoadsFrameNames,
									...tilerepoWoodenBridgeRoadsFrameNames,
									// TODO: add more roads
								]),
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.collect_coins_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_ONCLICK_COINS_ACCUMULATED,
							data: {
								"{number}": 75_000,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 400_000,
						[ResourceType.LUMBER]: 15_000,
						[ResourceType.STONE]: 2_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 50,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 13,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 270,
			},
		}),
	},
	14: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(250)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(280, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 140],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 46],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_towns_of_level",
							action: UpgradeObjectiveAction.SETTLEMENT_ON_LEVEL_COUNT,
							data: {
								"{number}": 12,
								"{level}": 12,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 500_000,
						[ResourceType.LUMBER]: 20_000,
						[ResourceType.STONE]: 5_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 55,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 14,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 280,
			},
		}),
	},
	15: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(275)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(300, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 150],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 48],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_clay_income",
							hint: "quest.hint_1028",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 100,
								"{resource}": ResourceType.CLAY_UNCAPPED_INCOME,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.clear_obstacles_around_town",
							action: UpgradeObjectiveAction.SETTLEMENT_CLEARED_RANGE,
							data: {
								"{number}": 4,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 600_000,
						[ResourceType.LUMBER]: 30_000,
						[ResourceType.STONE]: 7_500,
						[ResourceType.CLAY]: 1_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 60,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 15,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 290,
			},
		}),
	},
	16: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(300)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(320, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 160],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 50],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_fish_food_income",
							hint: "settlement.hint_16",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 175,
								"{resource}": ResourceType.FISH_INCOME, // Tu nie je uncapped, lebo fish income sa pocita inak.
							},
						},
					},
					price: {
						[ResourceType.COINS]: 700_000,
						[ResourceType.LUMBER]: 50_000,
						[ResourceType.STONE]: 15_000,
						[ResourceType.CLAY]: 3_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 65,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 16,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 300,
			},
		}),
	},
	17: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(325)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(340, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 170],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 52],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.explore_area_around_town",
							action: UpgradeObjectiveAction.SETTLEMENT_EXPLORED_RANGE,
							data: {
								"{number}": 5,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 800_000,
						[ResourceType.LUMBER]: 75_000,
						[ResourceType.STONE]: 25_000,
						[ResourceType.CLAY]: 5_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 70,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 17,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 310,
			},
		}),
	},
	18: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(350)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(360, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 190],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 54],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.research_upgrades",
							action: UpgradeObjectiveAction.UNLOCKED_UPGRADE_COUNT,
							data: {
								"{number}": 30,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 900_000,
						[ResourceType.LUMBER]: 100_000,
						[ResourceType.STONE]: 35_000,
						[ResourceType.CLAY]: 7_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 75,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 18,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 320,
			},
		}),
	},
	19: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(375)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(380, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 210],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 56],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_water_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 1_000,
								"{terrains}": tilerepoWaterFrameNames,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.collect_coins_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_ONCLICK_COINS_ACCUMULATED,
							data: {
								"{number}": 120_000,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.LUMBER]: 125_000,
						[ResourceType.STONE]: 50_000,
						[ResourceType.CLAY]: 10_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 80,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 19,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 330,
			},
		}),
	},
	20: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(400)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(400, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 230],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 58],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_iron_income",
							hint: "settlement.hint_20",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 150,
								"{resource}": ResourceType.IRON_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 1_500_000,
						[ResourceType.LUMBER]: 150_000,
						[ResourceType.STONE]: 65_000,
						[ResourceType.CLAY]: 15_000,
						[ResourceType.IRON]: 2_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 85,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 20,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 340,
			},
		}),
	},
	21: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(450)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(420, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 250],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 60],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_towns",
							action: UpgradeObjectiveAction.SETTLEMENT_COUNT,
							data: {
								"{number}": 25,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.have_2to4_buildings_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_BUILDING_COUNT,
							data: {
								"{2to4}": 4,
								"{buildings}":
									tilerepoSettlementBuildingsFrameNames,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 2_000_000,
						[ResourceType.LUMBER]: 175_000,
						[ResourceType.STONE]: 90_000,
						[ResourceType.CLAY]: 25_000,
						[ResourceType.IRON]: 5_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 90,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 21,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 350,
			},
		}),
	},
	22: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(500)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(440, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 275],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 62],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_lumber_income",
							hint: "quest.hint_1005",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 3_500,
								"{resource}":
									ResourceType.LUMBER_UNCAPPED_INCOME,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.clear_obstacles_around_town",
							action: UpgradeObjectiveAction.SETTLEMENT_CLEARED_RANGE,
							data: {
								"{number}": 5,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 2_500_000,
						[ResourceType.LUMBER]: 200_000,
						[ResourceType.STONE]: 110_000,
						[ResourceType.CLAY]: 40_000,
						[ResourceType.IRON]: 7_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 95,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 22,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 360,
			},
		}),
	},
	23: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(550)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(460, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 300],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 64],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_total_workforce",
							hint: "settlement.hint_11",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 15_000,
								"{resource}": ResourceType.POPULATION_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 3_000_000,
						[ResourceType.LUMBER]: 225_000,
						[ResourceType.STONE]: 130_000,
						[ResourceType.CLAY]: 65_000,
						[ResourceType.IRON]: 10_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 23,
			[FrameName.COBBLED_ROAD]: 0,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 370,
			},
		}),
	},
	24: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(600)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(480, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 325],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 66],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.research_upgrades",
							action: UpgradeObjectiveAction.UNLOCKED_UPGRADE_COUNT,
							data: {
								"{number}": 50,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.have_buildings_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_BUILDING_COUNT,
							data: {
								"{number}": 5,
								"{buildings}":
									tilerepoSettlementBuildingsFrameNames,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 3_500_000,
						[ResourceType.LUMBER]: 250_000,
						[ResourceType.STONE]: 150_000,
						[ResourceType.CLAY]: 90_000,
						[ResourceType.IRON]: 15_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 24,
			[FrameName.COBBLED_ROAD]: 5,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 380,
			},
		}),
	},
	25: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(650)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(500, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 350],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 68],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_goods_income",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 200,
								"{resource}":
									ResourceType.GOODS_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 4_000_000,
						[ResourceType.LUMBER]: 275_000,
						[ResourceType.STONE]: 175_000,
						[ResourceType.CLAY]: 110_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.GOODS]: 2_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 6,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 390,
			},
		}),
	},
	26: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(700)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(520, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 375],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 70],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 10_000,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.explore_area_around_town",
							action: UpgradeObjectiveAction.SETTLEMENT_EXPLORED_RANGE,
							data: {
								"{number}": 6,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 4_500_000,
						[ResourceType.LUMBER]: 300_000,
						[ResourceType.STONE]: 200_000,
						[ResourceType.CLAY]: 130_000,
						[ResourceType.IRON]: 40_000,
						[ResourceType.GOODS]: 5_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 7,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 400,
			},
		}),
	},
	27: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(750)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(540, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 400],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 72],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_stone_income",
							hint: "quest.hint_1019",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 2_500,
								"{resource}":
									ResourceType.STONE_UNCAPPED_INCOME,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.have_buildings_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_BUILDING_COUNT,
							data: {
								"{number}": 6,
								"{buildings}":
									tilerepoSettlementBuildingsFrameNames,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 5_000_000,
						[ResourceType.LUMBER]: 350_000,
						[ResourceType.STONE]: 225_000,
						[ResourceType.CLAY]: 150_000,
						[ResourceType.IRON]: 65_000,
						[ResourceType.GOODS]: 7_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 8,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 410,
			},
		}),
	},
	28: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(800)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(560, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 425],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 74],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_towns_of_level",
							action: UpgradeObjectiveAction.SETTLEMENT_ON_LEVEL_COUNT,
							data: {
								"{number}": 35,
								"{level}": 18,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 5_500_000,
						[ResourceType.LUMBER]: 400_000,
						[ResourceType.STONE]: 250_000,
						[ResourceType.CLAY]: 175_000,
						[ResourceType.IRON]: 90_000,
						[ResourceType.GOODS]: 10_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 9,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 420,
			},
		}),
	},
	29: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(850)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(580, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 450],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 76],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 12_500,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 6_000_000,
						[ResourceType.LUMBER]: 450_000,
						[ResourceType.STONE]: 275_000,
						[ResourceType.CLAY]: 200_000,
						[ResourceType.IRON]: 110_000,
						[ResourceType.GOODS]: 15_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 10,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 430,
			},
		}),
	},
	30: {
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, (): number => getCoinsProduction(900)],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(600, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 475],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 78],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_towns",
							action: UpgradeObjectiveAction.SETTLEMENT_COUNT,
							data: {
								"{number}": 40,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.have_buildings_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_BUILDING_COUNT,
							data: {
								"{number}": 7,
								"{buildings}":
									tilerepoSettlementBuildingsFrameNames,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 7_500_000,
						[ResourceType.LUMBER]: 500_000,
						[ResourceType.STONE]: 300_000,
						[ResourceType.CLAY]: 225_000,
						[ResourceType.IRON]: 130_000,
						[ResourceType.GOODS]: 25_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 10,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 450,
			},
		}),
	},
	31: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(1_000),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(620, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 500],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 80],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_steel_income",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 250,
								"{resource}":
									ResourceType.STEEL_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 10_000_000,
						[ResourceType.LUMBER]: 550_000,
						[ResourceType.STONE]: 350_000,
						[ResourceType.CLAY]: 250_000,
						[ResourceType.IRON]: 150_000,
						[ResourceType.GOODS]: 40_000,
						[ResourceType.STEEL]: 1_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 10,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 470,
			},
		}),
	},
	32: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(1_100),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(640, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 550],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 82],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_water_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 5_000,
								"{terrains}": tilerepoWaterFrameNames,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.collect_coins_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_ONCLICK_COINS_ACCUMULATED,
							data: {
								"{number}": 200_000,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 12_500_000,
						[ResourceType.LUMBER]: 600_000,
						[ResourceType.STONE]: 400_000,
						[ResourceType.CLAY]: 275_000,
						[ResourceType.IRON]: 175_000,
						[ResourceType.GOODS]: 65_000,
						[ResourceType.STEEL]: 2_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 10,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 490,
			},
		}),
	},
	33: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(1_200),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(660, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 600],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 84],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_lumber_income",
							hint: "quest.hint_1005",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 10_000,
								"{resource}":
									ResourceType.LUMBER_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 15_000_000,
						[ResourceType.LUMBER]: 650_000,
						[ResourceType.STONE]: 450_000,
						[ResourceType.CLAY]: 300_000,
						[ResourceType.IRON]: 200_000,
						[ResourceType.GOODS]: 90_000,
						[ResourceType.STEEL]: 3_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 10,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 510,
			},
		}),
	},
	34: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(1_300),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(680, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 650],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 86],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_clay_income",
							hint: "quest.hint_1028",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 2_250,
								"{resource}": ResourceType.CLAY_UNCAPPED_INCOME,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.have_buildings_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_BUILDING_COUNT,
							data: {
								"{number}": 8,
								"{buildings}":
									tilerepoSettlementBuildingsFrameNames,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 20_000_000,
						[ResourceType.LUMBER]: 700_000,
						[ResourceType.STONE]: 500_000,
						[ResourceType.CLAY]: 325_000,
						[ResourceType.IRON]: 225_000,
						[ResourceType.GOODS]: 110_000,
						[ResourceType.STEEL]: 5_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 10,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 530,
			},
		}),
	},
	35: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(1_400),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(700, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 700],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 88],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_gravel_roads",
							hint: "settlement.hint_13",
							action: UpgradeObjectiveAction.BUILDING_COUNT,
							data: {
								"{number}": 1_000,
								"{buildings}": new Set<FrameName>([
									...tilerepoGravelRoadsFrameNames,
									...tilerepoWoodenBridgeRoadsFrameNames,
									// TODO: add more roads
								]),
							},
						},
					},
					price: {
						[ResourceType.COINS]: 25_000_000,
						[ResourceType.LUMBER]: 750_000,
						[ResourceType.STONE]: 550_000,
						[ResourceType.CLAY]: 350_000,
						[ResourceType.IRON]: 250_000,
						[ResourceType.GOODS]: 130_000,
						[ResourceType.STEEL]: 10_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 10,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 550,
			},
		}),
	},
	36: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(1_500),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(720, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 750],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 90],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.explore_area_around_town",
							action: UpgradeObjectiveAction.SETTLEMENT_EXPLORED_RANGE,
							data: {
								"{number}": 7,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 30_000_000,
						[ResourceType.LUMBER]: 800_000,
						[ResourceType.STONE]: 600_000,
						[ResourceType.CLAY]: 375_000,
						[ResourceType.IRON]: 275_000,
						[ResourceType.GOODS]: 150_000,
						[ResourceType.STEEL]: 15_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 570,
			},
		}),
	},
	37: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(1_600),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(740, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 800],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 92],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_total_workforce",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 45_000,
								"{resource}": ResourceType.POPULATION_INCOME,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.clear_obstacles_around_town",
							action: UpgradeObjectiveAction.SETTLEMENT_CLEARED_RANGE,
							data: {
								"{number}": 7,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 35_000_000,
						[ResourceType.LUMBER]: 850_000,
						[ResourceType.STONE]: 650_000,
						[ResourceType.CLAY]: 400_000,
						[ResourceType.IRON]: 300_000,
						[ResourceType.GOODS]: 175_000,
						[ResourceType.STEEL]: 25_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 590,
			},
		}),
	},
	38: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(1_700),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(760, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 850],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 94],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_goods_income_from_tailor",
							action: UpgradeObjectiveAction.BUILDING_INCOME,
							data: {
								"{number}": 400,
								"{buildings}": new Set<FrameName>([
									FrameName.TAILORS_WORKSHOP,
								]),
								"{resource}": ResourceType.GOODS_INCOME,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.collect_coins_in_town",
							action: UpgradeObjectiveAction.SETTLEMENT_ONCLICK_COINS_ACCUMULATED,
							data: {
								"{number}": 300_000,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 40_000_000,
						[ResourceType.LUMBER]: 900_000,
						[ResourceType.STONE]: 700_000,
						[ResourceType.CLAY]: 450_000,
						[ResourceType.IRON]: 325_000,
						[ResourceType.GOODS]: 200_000,
						[ResourceType.STEEL]: 40_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 610,
			},
		}),
	},
	39: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(1_800),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(780, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 900],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 96],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_towns_of_level",
							action: UpgradeObjectiveAction.SETTLEMENT_ON_LEVEL_COUNT,
							data: {
								"{number}": 45,
								"{level}": 25,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 45_000_000,
						[ResourceType.LUMBER]: 950_000,
						[ResourceType.STONE]: 750_000,
						[ResourceType.CLAY]: 500_000,
						[ResourceType.IRON]: 350_000,
						[ResourceType.GOODS]: 225_000,
						[ResourceType.STEEL]: 65_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 630,
			},
		}),
	},
	40: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(1_900),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(800, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 950],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 98],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_goods_income",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 2_000,
								"{resource}":
									ResourceType.GOODS_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 50_000_000,
						[ResourceType.LUMBER]: 1_000_000,
						[ResourceType.STONE]: 800_000,
						[ResourceType.CLAY]: 550_000,
						[ResourceType.IRON]: 375_000,
						[ResourceType.GOODS]: 250_000,
						[ResourceType.STEEL]: 90_000,
						[ResourceType.ARMY]: 250,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 650,
			},
		}),
	},
	41: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(2_000),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(820, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 1_000],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 100],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_buildings",
							action: UpgradeObjectiveAction.BUILDING_COUNT,
							data: {
								"{number}": 20,
								"{buildings}": new Set<FrameName>([
									FrameName.UNIVERSITY,
								]),
							},
						},
					},
					price: {
						[ResourceType.COINS]: 55_000_000,
						[ResourceType.LUMBER]: 1_100_000,
						[ResourceType.STONE]: 850_000,
						[ResourceType.CLAY]: 600_000,
						[ResourceType.IRON]: 400_000,
						[ResourceType.GOODS]: 275_000,
						[ResourceType.STEEL]: 110_000,
						[ResourceType.ARMY]: 500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 670,
			},
		}),
	},
	42: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(2_100),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(840, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 1_100],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 102],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_cobbled_roads",
							hint: "settlement.hint_27",
							action: UpgradeObjectiveAction.BUILDING_COUNT,
							data: {
								"{number}": 400,
								"{buildings}": new Set<FrameName>([
									...tilerepoCobbledRoadsFrameNames,
									...tilerepoStoneBridgeRoadsFrameNames,
									...tilerepoTunnelRoadsFrameNames,
								]),
							},
						},
					},
					price: {
						[ResourceType.COINS]: 60_000_000,
						[ResourceType.LUMBER]: 1_200_000,
						[ResourceType.STONE]: 900_000,
						[ResourceType.CLAY]: 650_000,
						[ResourceType.IRON]: 450_000,
						[ResourceType.GOODS]: 300_000,
						[ResourceType.STEEL]: 130_000,
						[ResourceType.ARMY]: 750,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 690,
			},
		}),
	},
	43: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(2_200),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(860, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 1_200],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 104],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_buildings",
							action: UpgradeObjectiveAction.BUILDING_COUNT,
							data: {
								"{number}": 30,
								"{buildings}": new Set<FrameName>([
									FrameName.BARRACKS,
								]),
							},
						},
					},
					price: {
						[ResourceType.COINS]: 65_000_000,
						[ResourceType.LUMBER]: 1_300_000,
						[ResourceType.STONE]: 950_000,
						[ResourceType.CLAY]: 700_000,
						[ResourceType.IRON]: 500_000,
						[ResourceType.GOODS]: 325_000,
						[ResourceType.STEEL]: 150_000,
						[ResourceType.ARMY]: 1_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 710,
			},
		}),
	},
	44: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(2_300),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(880, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 1_300],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 106],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 25_000,
							},
						},
						[UpgradeObjectiveType.SIDE]: {
							text: "task.explore_area_around_town",
							action: UpgradeObjectiveAction.SETTLEMENT_EXPLORED_RANGE,
							data: {
								"{number}": 8,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 70_000_000,
						[ResourceType.LUMBER]: 1_400_000,
						[ResourceType.STONE]: 1_000_000,
						[ResourceType.CLAY]: 750_000,
						[ResourceType.IRON]: 550_000,
						[ResourceType.GOODS]: 350_000,
						[ResourceType.STEEL]: 175_000,
						[ResourceType.ARMY]: 1_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 730,
			},
		}),
	},
	45: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(2_400),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(900, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 1_400],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 108],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_coins_income_from_inns",
							action: UpgradeObjectiveAction.BUILDING_INCOME,
							data: {
								"{number}": 3_750,
								"{buildings}": new Set<FrameName>([
									FrameName.INN,
								]),
								"{resource}": ResourceType.COINS_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 75_000_000,
						[ResourceType.LUMBER]: 1_500_000,
						[ResourceType.STONE]: 1_100_000,
						[ResourceType.CLAY]: 800_000,
						[ResourceType.IRON]: 600_000,
						[ResourceType.GOODS]: 375_000,
						[ResourceType.STEEL]: 200_000,
						[ResourceType.ARMY]: 2_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 750,
			},
		}),
	},
	46: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(2_500),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(920, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 1_500],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 110],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_cobbled_roads",
							hint: "settlement.hint_27",
							action: UpgradeObjectiveAction.BUILDING_COUNT,
							data: {
								"{number}": 700,
								"{buildings}": new Set<FrameName>([
									...tilerepoCobbledRoadsFrameNames,
									...tilerepoStoneBridgeRoadsFrameNames,
									...tilerepoTunnelRoadsFrameNames,
								]),
							},
						},
					},
					price: {
						[ResourceType.COINS]: 80_000_000,
						[ResourceType.LUMBER]: 1_750_000,
						[ResourceType.STONE]: 1_200_000,
						[ResourceType.CLAY]: 850_000,
						[ResourceType.IRON]: 650_000,
						[ResourceType.GOODS]: 400_000,
						[ResourceType.STEEL]: 225_000,
						[ResourceType.ARMY]: 5_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 770,
			},
		}),
	},
	47: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(2_600),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(940, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 1_625],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 112],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_steel_income",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 1_000,
								"{resource}":
									ResourceType.STEEL_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 85_000_000,
						[ResourceType.LUMBER]: 2_000_000,
						[ResourceType.STONE]: 1_300_000,
						[ResourceType.CLAY]: 900_000,
						[ResourceType.IRON]: 700_000,
						[ResourceType.GOODS]: 450_000,
						[ResourceType.STEEL]: 250_000,
						[ResourceType.ARMY]: 7_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 790,
			},
		}),
	},
	48: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(2_700),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(960, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 1_750],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 114],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_fish_food_income",
							hint: "settlement.hint_16",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 7_500,
								"{resource}": ResourceType.FISH_INCOME, // Tu nie je uncapped, lebo fish income sa pocita inak.
							},
						},
					},
					price: {
						[ResourceType.COINS]: 90_000_000,
						[ResourceType.LUMBER]: 2_250_000,
						[ResourceType.STONE]: 1_400_000,
						[ResourceType.CLAY]: 950_000,
						[ResourceType.IRON]: 750_000,
						[ResourceType.GOODS]: 500_000,
						[ResourceType.STEEL]: 275_000,
						[ResourceType.ARMY]: 10_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 810,
			},
		}),
	},
	49: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(2_850),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(980, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 1_875],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 116],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_towns",
							action: UpgradeObjectiveAction.SETTLEMENT_COUNT,
							data: {
								"{number}": 75,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 100_000_000,
						[ResourceType.LUMBER]: 2_500_000,
						[ResourceType.STONE]: 1_500_000,
						[ResourceType.CLAY]: 1_000_000,
						[ResourceType.IRON]: 800_000,
						[ResourceType.GOODS]: 550_000,
						[ResourceType.STEEL]: 300_000,
						[ResourceType.ARMY]: 12_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 12,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 830,
			},
		}),
	},
	50: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(3_000),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_000, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 2_000],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 118],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_total_workforce",
							hint: "settlement.hint_11",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 100_000,
								"{resource}": ResourceType.POPULATION_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 110_000_000,
						[ResourceType.LUMBER]: 2_750_000,
						[ResourceType.STONE]: 1_600_000,
						[ResourceType.CLAY]: 1_050_000,
						[ResourceType.IRON]: 850_000,
						[ResourceType.GOODS]: 600_000,
						[ResourceType.STEEL]: 325_000,
						[ResourceType.ARMY]: 15_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 850,
			},
		}),
	},
	51: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(3_100),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_040, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 2_250],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 120],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 40_000,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 120_000_000,
						[ResourceType.LUMBER]: 3_000_000,
						[ResourceType.STONE]: 1_700_000,
						[ResourceType.CLAY]: 1_100_000,
						[ResourceType.IRON]: 900_000,
						[ResourceType.GOODS]: 650_000,
						[ResourceType.STEEL]: 350_000,
						[ResourceType.ARMY]: 17_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 870,
			},
		}),
	},
	52: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(3_200),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_080, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 2_500],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 122],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_army_income",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 50,
								"{resource}": ResourceType.ARMY_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 130_000_000,
						[ResourceType.LUMBER]: 3_250_000,
						[ResourceType.STONE]: 1_800_000,
						[ResourceType.CLAY]: 1_150_000,
						[ResourceType.IRON]: 950_000,
						[ResourceType.GOODS]: 700_000,
						[ResourceType.STEEL]: 375_000,
						[ResourceType.ARMY]: 20_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 890,
			},
		}),
	},
	53: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(3_300),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_120, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 2_750],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 124],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_buildings",
							hint: "settlement.hint_53",
							action: UpgradeObjectiveAction.BUILDING_COUNT,
							data: {
								"{number}": 10,
								"{buildings}": new Set<FrameName>([
									FrameName.MOUNTAIN_BELLOWS,
								]),
							},
						},
					},
					price: {
						[ResourceType.COINS]: 140_000_000,
						[ResourceType.LUMBER]: 3_500_000,
						[ResourceType.STONE]: 1_900_000,
						[ResourceType.CLAY]: 1_200_000,
						[ResourceType.IRON]: 1_000_000,
						[ResourceType.GOODS]: 750_000,
						[ResourceType.STEEL]: 400_000,
						[ResourceType.ARMY]: 22_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 910,
			},
		}),
	},
	54: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(3_400),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_160, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 3_000],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 126],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_towns",
							action: UpgradeObjectiveAction.SETTLEMENT_COUNT,
							data: {
								"{number}": 105,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 150_000_000,
						[ResourceType.LUMBER]: 3_750_000,
						[ResourceType.STONE]: 2_000_000,
						[ResourceType.CLAY]: 1_250_000,
						[ResourceType.IRON]: 1_050_000,
						[ResourceType.GOODS]: 800_000,
						[ResourceType.STEEL]: 425_000,
						[ResourceType.ARMY]: 25_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 930,
			},
		}),
	},
	55: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(3_500),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_200, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 3_250],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 128],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_coin_income",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 350_000,
								"{resource}":
									ResourceType.COINS_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 160_000_000,
						[ResourceType.LUMBER]: 4_000_000,
						[ResourceType.STONE]: 2_100_000,
						[ResourceType.CLAY]: 1_300_000,
						[ResourceType.IRON]: 1_100_000,
						[ResourceType.GOODS]: 850_000,
						[ResourceType.STEEL]: 450_000,
						[ResourceType.ARMY]: 27_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 950,
			},
		}),
	},
	56: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(3_600),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_250, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 3_500],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 130],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_iron_income",
							hint: "settlement.hint_20",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 7_500,
								"{resource}": ResourceType.IRON_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 170_000_000,
						[ResourceType.LUMBER]: 4_250_000,
						[ResourceType.STONE]: 2_200_000,
						[ResourceType.CLAY]: 1_350_000,
						[ResourceType.IRON]: 1_150_000,
						[ResourceType.GOODS]: 900_000,
						[ResourceType.STEEL]: 475_000,
						[ResourceType.ARMY]: 30_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 970,
			},
		}),
	},
	57: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(3_700),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_300, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 3_750],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 132],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_towns_of_level",
							action: UpgradeObjectiveAction.SETTLEMENT_ON_LEVEL_COUNT,
							data: {
								"{number}": 20,
								"{level}": 45,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 180_000_000,
						[ResourceType.LUMBER]: 4_500_000,
						[ResourceType.STONE]: 2_300_000,
						[ResourceType.CLAY]: 1_400_000,
						[ResourceType.IRON]: 1_200_000,
						[ResourceType.GOODS]: 950_000,
						[ResourceType.STEEL]: 500_000,
						[ResourceType.ARMY]: 32_500,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 990,
			},
		}),
	},
	58: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(3_800),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_350, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 4_000],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 134],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_pois",
							action: UpgradeObjectiveAction.BUILDING_COUNT,
							data: {
								"{number}": 28,
								"{buildings}": tilerepoPOIs1FrameNames,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 190_000_000,
						[ResourceType.LUMBER]: 4_750_000,
						[ResourceType.STONE]: 2_400_000,
						[ResourceType.CLAY]: 1_450_000,
						[ResourceType.IRON]: 1_250_000,
						[ResourceType.GOODS]: 1_000_000,
						[ResourceType.STEEL]: 525_000,
						[ResourceType.ARMY]: 35_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_010,
			},
		}),
	},
	59: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(3_900),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_400, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 4_250],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 136],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_stone_income",
							hint: "quest.hint_1019",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 12_500,
								"{resource}":
									ResourceType.STONE_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 200_000_000,
						[ResourceType.LUMBER]: 5_000_000,
						[ResourceType.STONE]: 2_500_000,
						[ResourceType.CLAY]: 1_500_000,
						[ResourceType.IRON]: 1_300_000,
						[ResourceType.GOODS]: 1_050_000,
						[ResourceType.STEEL]: 550_000,
						[ResourceType.ARMY]: 40_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_030,
			},
		}),
	},
	60: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(4_000),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_450, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 4_500],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 138],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_goods_income",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 10_000,
								"{resource}":
									ResourceType.GOODS_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 225_000_000,
						[ResourceType.LUMBER]: 5_250_000,
						[ResourceType.STONE]: 2_600_000,
						[ResourceType.CLAY]: 1_550_000,
						[ResourceType.IRON]: 1_350_000,
						[ResourceType.GOODS]: 1_100_000,
						[ResourceType.STEEL]: 575_000,
						[ResourceType.ARMY]: 45_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_050,
			},
		}),
	},
	61: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(4_100),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_500, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 4_750],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 140],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 60_000,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 250_000_000,
						[ResourceType.LUMBER]: 5_500_000,
						[ResourceType.STONE]: 2_700_000,
						[ResourceType.CLAY]: 1_600_000,
						[ResourceType.IRON]: 1_400_000,
						[ResourceType.GOODS]: 1_150_000,
						[ResourceType.STEEL]: 600_000,
						[ResourceType.ARMY]: 50_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_070,
			},
		}),
	},
	62: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(4_200),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_550, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 5_000],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 142],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_coins_income_from_inns",
							action: UpgradeObjectiveAction.BUILDING_INCOME,
							data: {
								"{number}": 12_000,
								"{buildings}": new Set<FrameName>([
									FrameName.INN,
								]),
								"{resource}": ResourceType.COINS_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 300_000_000,
						[ResourceType.LUMBER]: 5_750_000,
						[ResourceType.STONE]: 2_800_000,
						[ResourceType.CLAY]: 1_650_000,
						[ResourceType.IRON]: 1_450_000,
						[ResourceType.GOODS]: 1_200_000,
						[ResourceType.STEEL]: 650_000,
						[ResourceType.ARMY]: 55_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_090,
			},
		}),
	},
	63: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(4_300),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_600, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 5_250],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 144],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_coins_income_from_pearl_hunters",
							action: UpgradeObjectiveAction.BUILDING_INCOME,
							data: {
								"{number}": 10_000,
								"{buildings}": new Set<FrameName>([
									FrameName.SHALLOW_SEA_PEARL_HUNTER,
									FrameName.DEEP_SEA_PEARL_HUNTER,
								]),
								"{resource}": ResourceType.COINS_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 325_000_000,
						[ResourceType.LUMBER]: 6_000_000,
						[ResourceType.STONE]: 2_900_000,
						[ResourceType.CLAY]: 1_700_000,
						[ResourceType.IRON]: 1_500_000,
						[ResourceType.GOODS]: 1_250_000,
						[ResourceType.STEEL]: 700_000,
						[ResourceType.ARMY]: 60_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_110,
			},
		}),
	},
	64: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(4_400),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_650, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 5_500],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 146],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_steel_income",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 3_000,
								"{resource}":
									ResourceType.STEEL_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 350_000_000,
						[ResourceType.LUMBER]: 6_250_000,
						[ResourceType.STONE]: 3_000_000,
						[ResourceType.CLAY]: 1_750_000,
						[ResourceType.IRON]: 1_550_000,
						[ResourceType.GOODS]: 1_300_000,
						[ResourceType.STEEL]: 750_000,
						[ResourceType.ARMY]: 65_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_130,
			},
		}),
	},
	65: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(4_500),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_700, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 5_750],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 148],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_total_workforce",
							hint: "settlement.hint_11",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 250_000,
								"{resource}": ResourceType.POPULATION_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 400_000_000,
						[ResourceType.LUMBER]: 6_500_000,
						[ResourceType.STONE]: 3_200_000,
						[ResourceType.CLAY]: 1_850_000,
						[ResourceType.IRON]: 1_600_000,
						[ResourceType.GOODS]: 1_350_000,
						[ResourceType.STEEL]: 800_000,
						[ResourceType.ARMY]: 70_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_150,
			},
		}),
	},
	66: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(4_600),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_750, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 6_000],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 150],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_water_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 35_000,
								"{terrains}": tilerepoWaterFrameNames,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 450_000_000,
						[ResourceType.LUMBER]: 6_750_000,
						[ResourceType.STONE]: 3_400_000,
						[ResourceType.CLAY]: 1_950_000,
						[ResourceType.IRON]: 1_650_000,
						[ResourceType.GOODS]: 1_400_000,
						[ResourceType.STEEL]: 850_000,
						[ResourceType.ARMY]: 75_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_200,
			},
		}),
	},
	67: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(4_700),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_800, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 6_250],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 152],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.control_towns_of_level",
							action: UpgradeObjectiveAction.SETTLEMENT_ON_LEVEL_COUNT,
							data: {
								"{number}": 15,
								"{level}": 55,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 500_000_000,
						[ResourceType.LUMBER]: 7_000_000,
						[ResourceType.STONE]: 3_600_000,
						[ResourceType.CLAY]: 2_050_000,
						[ResourceType.IRON]: 1_700_000,
						[ResourceType.GOODS]: 1_450_000,
						[ResourceType.STEEL]: 900_000,
						[ResourceType.ARMY]: 80_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_250,
			},
		}),
	},
	68: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(4_800),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_850, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 6_500],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 154],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.research_upgrades",
							action: UpgradeObjectiveAction.UNLOCKED_UPGRADE_COUNT,
							data: {
								"{number}": 180,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 600_000_000,
						[ResourceType.LUMBER]: 7_250_000,
						[ResourceType.STONE]: 3_800_000,
						[ResourceType.CLAY]: 2_150_000,
						[ResourceType.IRON]: 1_750_000,
						[ResourceType.GOODS]: 1_500_000,
						[ResourceType.STEEL]: 950_000,
						[ResourceType.ARMY]: 85_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_300,
			},
		}),
	},
	69: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(4_900),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_900, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 6_750],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 156],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_buildings",
							action: UpgradeObjectiveAction.BUILDING_COUNT,
							data: {
								"{number}": 5,
								"{buildings}": new Set<FrameName>([
									FrameName.STABLES,
								]),
							},
						},
					},
					price: {
						[ResourceType.COINS]: 700_000_000,
						[ResourceType.LUMBER]: 7_500_000,
						[ResourceType.STONE]: 4_000_000,
						[ResourceType.CLAY]: 2_250_000,
						[ResourceType.IRON]: 1_800_000,
						[ResourceType.GOODS]: 1_550_000,
						[ResourceType.STEEL]: 1_000_000,
						[ResourceType.ARMY]: 90_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_400,
			},
		}),
	},
	70: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(5_000),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(1_950, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 7_000],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 158],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_explored_tiles",
							action: UpgradeObjectiveAction.EXPLORED_COUNT,
							data: {
								"{number}": 80_000,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 800_000_000,
						[ResourceType.LUMBER]: 8_000_000,
						[ResourceType.STONE]: 4_250_000,
						[ResourceType.CLAY]: 2_350_000,
						[ResourceType.IRON]: 1_850_000,
						[ResourceType.GOODS]: 1_600_000,
						[ResourceType.STEEL]: 1_050_000,
						[ResourceType.ARMY]: 95_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_500,
			},
		}),
	},
	71: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(5_100),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(2_000, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 7_250],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 160],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_settlement_level_accumulated",
							action: UpgradeObjectiveAction.SETTLEMENT_LEVEL_ACCUMULATED,
							data: {
								"{number}": 4_750,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 900_000_000,
						[ResourceType.LUMBER]: 8_500_000,
						[ResourceType.STONE]: 4_500_000,
						[ResourceType.CLAY]: 2_450_000,
						[ResourceType.IRON]: 1_900_000,
						[ResourceType.GOODS]: 1_650_000,
						[ResourceType.STEEL]: 1_100_000,
						[ResourceType.ARMY]: 100_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_600,
			},
		}),
	},
	72: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(5_200),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(2_050, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 7_500],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 162],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_coin_income",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 600_000,
								"{resource}":
									ResourceType.COINS_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 1_000_000_000,
						[ResourceType.LUMBER]: 9_000_000,
						[ResourceType.STONE]: 4_750_000,
						[ResourceType.CLAY]: 2_550_000,
						[ResourceType.IRON]: 1_950_000,
						[ResourceType.GOODS]: 1_700_000,
						[ResourceType.STEEL]: 1_150_000,
						[ResourceType.ARMY]: 105_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_700,
			},
		}),
	},
	73: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(5_300),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(2_100, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 7_750],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 164],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_fish_food_income",
							hint: "settlement.hint_16",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 15_000,
								"{resource}": ResourceType.FISH_INCOME, // Tu nie je uncapped, lebo fish income sa pocita inak.
							},
						},
					},
					price: {
						[ResourceType.COINS]: 1_250_000_000,
						[ResourceType.LUMBER]: 9_500_000,
						[ResourceType.STONE]: 5_000_000,
						[ResourceType.CLAY]: 2_650_000,
						[ResourceType.IRON]: 2_000_000,
						[ResourceType.GOODS]: 1_750_000,
						[ResourceType.STEEL]: 1_200_000,
						[ResourceType.ARMY]: 110_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_800,
			},
		}),
	},
	74: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(5_400),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(2_150, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 8_000],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 166],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.SETTLEMENT_CASTLE,
				{
					objective: {
						[UpgradeObjectiveType.MAIN]: {
							text: "task.have_army_income",
							action: UpgradeObjectiveAction.INCOME,
							data: {
								"{number}": 100,
								"{resource}": ResourceType.ARMY_UNCAPPED_INCOME,
							},
						},
					},
					price: {
						[ResourceType.COINS]: 1_500_000_000,
						[ResourceType.LUMBER]: 10_000_000,
						[ResourceType.STONE]: 5_500_000,
						[ResourceType.CLAY]: 2_750_000,
						[ResourceType.IRON]: 2_100_000,
						[ResourceType.GOODS]: 1_800_000,
						[ResourceType.STEEL]: 1_250_000,
						[ResourceType.ARMY]: 115_000,
					},
					skipPrice: {
						[ResourceType.GEMS]: 100,
					},
				},
			],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 1_900,
			},
		}),
	},
	75: {
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => getCoinsProduction(5_500),
			],
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number =>
					getPopulationProduction(2_200, entity),
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 8_250],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CLICKS, 168],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			// [
			// 	FrameName.SETTLEMENT_CASTLE,
			// 	{
			// 		objective: {
			// 			[UpgradeObjectiveType.MAIN]: {
			// 				text: "task.have_army_income",
			// 				action: UpgradeObjectiveAction.INCOME,
			// 				data: {
			// 					"{number}": 100,
			// 					"{resource}": ResourceType.ARMY_UNCAPPED_INCOME,
			// 				},
			// 			},
			// 		},
			// 		price: {
			// 			[ResourceType.COINS]: 1_500_000_000,
			// 			[ResourceType.LUMBER]: 10_000_000,
			// 			[ResourceType.STONE]: 5_500_000,
			// 			[ResourceType.CLAY]: 2_750_000,
			// 			[ResourceType.IRON]: 2_100_000,
			// 			[ResourceType.GOODS]: 1_800_000,
			// 			[ResourceType.STEEL]: 1_250_000,
			// 			[ResourceType.ARMY]: 115_000,
			// 		},
			// 		skipPrice: {
			// 			[ResourceType.GEMS]: 100,
			// 		},
			// 	},
			// ],
		]),
		roadCaps: {
			[FrameName.GRAVEL_ROAD]: 25,
			[FrameName.COBBLED_ROAD]: 15,
		},
		onClick: (): TileEntityOnClickMethodReturnType => ({
			reward: {
				[ResourceType.COINS]: 2_000,
			},
		}),
	},
};

export const getSettlementConfigByLevel = (
	level: number,
): SettlementConfigItem => {
	if (!config[level]) throw new Error(`Settlement level ${level} not found`);
	return config[level];
};

export const MAX_SETTLEMENT_LEVEL = Math.max(
	// eslint-disable-next-line no-restricted-syntax
	...Object.keys(config).map(Number),
);
